import { useCallback, useMemo, useState } from "react";
import { useUser } from "@clerk/clerk-react";
import { useLocation } from "react-router-dom";
import { pageGuidanceIdForPath, readPageGuidance, writePageGuidance } from "@/lib/pageGuidance";
import { PageGuidanceContext } from "@/contexts/pageGuidanceContextValue";

export function PageGuidanceProvider({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  const { user } = useUser();
  const pageId = pageGuidanceIdForPath(pathname);
  const userId = user?.id ?? null;
  const stateKey = userId && pageId ? `${userId}:${pageId}` : null;
  const [overrides, setOverrides] = useState<Record<string, boolean>>({});

  const enabled =
    stateKey && userId && pageId
      ? (overrides[stateKey] ?? readPageGuidance(userId, pageId))
      : false;

  const setEnabled = useCallback(
    (next: boolean) => {
      if (!stateKey || !userId || !pageId) return;
      setOverrides((current) => ({ ...current, [stateKey]: next }));
      writePageGuidance(userId, pageId, next);
    },
    [pageId, setOverrides, stateKey, userId],
  );

  const value = useMemo(
    () => ({ pageId, enabled, setEnabled, provided: true }),
    [enabled, pageId, setEnabled],
  );

  return <PageGuidanceContext.Provider value={value}>{children}</PageGuidanceContext.Provider>;
}
